import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  Bell,
  AlertTriangle,
  Info,
  CheckCircle,
  Settings,
  Package,
  BarChart3
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/quill-custom.css';
import Modal from './Modal';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface Announcement {
  id: number;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger' | 'feature' | 'maintenance';
  target_roles: string[] | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  priority: number;
  show_on_dashboard: boolean;
  show_as_popup: boolean;
  icon: string;
  created_by: number;
  created_by_name: string;
  read_count?: number;
  dismissed_count?: number;
  created_at: string;
  updated_at: string;
}

// React Quill configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image', 'video'],
    ['blockquote', 'code-block'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'align', 'link', 'image', 'video', 'blockquote', 'code-block'
];

const AnnouncementsPage: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAnnouncement, setViewingAnnouncement] = useState<Announcement | null>(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showNotification } = useNotifications();
  const { t } = useLanguage();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info' as const,
    target_roles: [] as string[],
    is_active: true,
    start_date: '',
    end_date: '',
    priority: 1,
    show_on_dashboard: true,
    show_as_popup: false,
    icon: 'bell'
  });

  const typeOptions = [
    { value: 'info', label: t('announce.type.info'), icon: Info, color: 'text-primary' },
    { value: 'success', label: t('announce.type.success'), icon: CheckCircle, color: 'text-green-600' },
    { value: 'warning', label: t('announce.type.warning'), icon: AlertTriangle, color: 'text-yellow-600' },
    { value: 'danger', label: t('announce.type.danger'), icon: AlertTriangle, color: 'text-red-600' },
    { value: 'feature', label: t('announce.type.feature'), icon: Package, color: 'text-purple-600' },
    { value: 'maintenance', label: t('announce.type.maintenance'), icon: Settings, color: 'text-gray-600 dark:text-gray-400' }
  ];

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'superadmin', label: t('announce.role.superadmin') },
    { value: 'user', label: t('announce.role.user') },
    { value: 'technician', label: t('announce.role.technician') }
  ];

  const iconOptions = [
    'bell', 'info', 'alert-triangle', 'check-circle', 'settings', 'package', 'tools', 'cog', 'star', 'heart'
  ];

  const fetchAnnouncements = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        search: searchTerm,
        type: typeFilter,
        status: statusFilter
      });

      const response = await apiService.get(`/announcements?${params}`);
      setAnnouncements(response?.data || []);
      setTotalPages(response?.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      showNotification(t('announce.errLoad'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage, searchTerm, typeFilter, statusFilter]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'info',
      target_roles: [],
      is_active: true,
      start_date: '',
      end_date: '',
      priority: 1,
      show_on_dashboard: true,
      show_as_popup: false,
      icon: 'bell'
    });
    setEditingAnnouncement(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Tarihleri kontrol et - boş string yerine null gönder, geçerli tarih ise YYYY-MM-DD formatında gönder
      const processDate = (dateStr: string): string | null => {
        if (!dateStr || dateStr.trim() === '') return null;
        // YYYY-MM-DD formatını doğrula
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(dateStr)) {
          return dateStr;
        }
        return null;
      };

      const payload = {
        ...formData,
        target_roles: (formData.target_roles && Array.isArray(formData.target_roles) && formData.target_roles.length > 0) ? formData.target_roles : null,
        start_date: processDate(formData.start_date),
        end_date: processDate(formData.end_date),
        is_active: Boolean(formData.is_active),
        show_on_dashboard: Boolean(formData.show_on_dashboard),
        show_as_popup: Boolean(formData.show_as_popup),
        priority: Number(formData.priority)
      };

      console.log('Announcement payload:', {
        start_date: formData.start_date,
        end_date: formData.end_date,
        processedStart: payload.start_date,
        processedEnd: payload.end_date
      });

      if (editingAnnouncement) {
        await apiService.put(`/announcements/${editingAnnouncement.id}`, payload);
        showNotification(t('announce.successUpdate'), 'success');
      } else {
        await apiService.post('/announcements', payload);
        showNotification(t('announce.successCreate'), 'success');
      }

      setShowModal(false);
      resetForm();
      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error saving announcement:', error);
      showNotification(
        error.response?.data?.error || t('announce.errSave'),
        'error'
      );
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      target_roles: announcement.target_roles || [],
      is_active: announcement.is_active,
      start_date: announcement.start_date ? announcement.start_date.split('T')[0].split(' ')[0] : '',
      end_date: announcement.end_date ? announcement.end_date.split('T')[0].split(' ')[0] : '',
      priority: announcement.priority,
      show_on_dashboard: announcement.show_on_dashboard,
      show_as_popup: announcement.show_as_popup,
      icon: announcement.icon
    });
    setShowModal(true);
  };

  const handleView = (announcement: Announcement) => {
    setViewingAnnouncement(announcement);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (announcement: Announcement) => {
    try {
      const response = await apiService.patch(`/announcements/${announcement.id}/toggle-status`, {});
      showNotification(response.message, 'success');
      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error toggling announcement status:', error);
      showNotification(
        error.response?.data?.error || t('announce.errToggle'),
        'error'
      );
    }
  };

  const handleDelete = async (announcement: Announcement) => {
    if (!window.confirm(t('announce.confirmDelete').replace('{title}', announcement.title))) {
      return;
    }

    try {
      await apiService.delete(`/announcements/${announcement.id}`);
      showNotification(t('announce.successDelete'), 'success');
      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error deleting announcement:', error);
      showNotification(
        error.response?.data?.error || t('announce.errDelete'),
        'error'
      );
    }
  };

  const getTypeConfig = (type: string) => {
    return typeOptions.find(opt => opt.value === type) || typeOptions[0];
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <Bell className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">
              {t('announce.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 text-sm sm:text-base">
              {t('announce.subtitle')}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-primary hover:bg-primary-dark text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span>{t('announce.newBtn')}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('announce.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white min-h-[44px]"
              />
            </div>
          </div>
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="">{t('announce.allTypes')}</option>
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="">{t('announce.allStatuses')}</option>
              <option value="active">{t('announce.statusActive')}</option>
              <option value="inactive">{t('announce.statusInactive')}</option>
            </select>
          </div>
          <div className="flex items-center justify-end">
            <Filter className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300">
              {t('announce.count').replace('{count}', String(announcements?.length || 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Announcements Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colTitle')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colDates')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colStats')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('announce.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {announcements?.length > 0 ? (
                announcements.map((announcement) => {
                  const typeConfig = getTypeConfig(announcement.type);
                  const IconComponent = typeConfig.icon;
                  
                  return (
                    <tr key={announcement.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3 ${typeConfig.color}`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div>
                            <button
                              onClick={() => handleView(announcement)}
                              className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white max-w-xs truncate hover:text-primary transition-colors"
                            >
                              {announcement.title}
                            </button>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {announcement.created_by_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeConfig.color.replace('text-', 'bg-').replace('-600', '-100')} ${typeConfig.color} dark:bg-opacity-20`}>
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {(() => {
                            const now = new Date();
                            const start = announcement.start_date ? new Date(announcement.start_date) : null;
                            const end = announcement.end_date ? new Date(announcement.end_date) : null;

                            // Determine effective status
                            let effectiveStatus: 'active' | 'inactive' | 'pending' | 'expired' = 'inactive';
                            let statusLabel = t('announce.statusInactive');
                            let statusStyle = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
                            let dotColor = 'bg-red-600';

                            if (announcement.is_active) {
                              if (start && start > now) {
                                effectiveStatus = 'pending';
                                statusLabel = t('announce.statusPending');
                                statusStyle = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
                                dotColor = 'bg-yellow-600';
                              } else if (end && end < now) {
                                effectiveStatus = 'expired';
                                statusLabel = t('announce.statusExpired');
                                statusStyle = 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200';
                                dotColor = 'bg-orange-600';
                              } else {
                                effectiveStatus = 'active';
                                statusLabel = t('announce.statusActive');
                                statusStyle = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
                                dotColor = 'bg-green-600';
                              }
                            }

                            return (
                              <>
                                <button
                                  onClick={() => handleToggleStatus(announcement)}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusStyle}`}
                                  title={announcement.is_active ? t('announce.titleToggleActive') : t('announce.titleToggleInactive')}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${dotColor}`}></span>
                                  {statusLabel}
                                </button>
                                {effectiveStatus === 'active' && (
                                  <span className="text-[10px] text-primary dark:text-primary font-medium">
                                    {t('announce.live')}
                                  </span>
                                )}
                                {effectiveStatus === 'pending' && (
                                  <span className="text-[10px] text-yellow-600 dark:text-yellow-400 font-medium">
                                    {`• ${start?.toLocaleDateString()} `}
                                  </span>
                                )}
                                {effectiveStatus === 'expired' && (
                                  <span className="text-[10px] text-orange-600 dark:text-orange-400 font-medium">
                                    {`• ${end?.toLocaleDateString()} `}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <div>B: {announcement.start_date ? new Date(announcement.start_date).toLocaleDateString('tr-TR') : '-'}</div>
                          <div>S: {announcement.end_date ? new Date(announcement.end_date).toLocaleDateString('tr-TR') : '-'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center" title="Görüntülenme">
                            <Eye className="h-3 w-3 mr-1" />
                            {announcement.read_count || 0}
                          </div>
                          <div className="flex items-center" title="Öncelik">
                            <BarChart3 className="h-3 w-3 mr-1" />
                            {announcement.priority}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleView(announcement)}
                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                            title="İçeriği Görüntüle"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(announcement)}
                            className="p-1 text-gray-400 hover:text-amber-600 transition-colors"
                            title="Düzenle"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(announcement)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                    {t('announce.noAnnouncements')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <div className="flex space-x-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 rounded-md ${
                  currentPage === page
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                  {editingAnnouncement ? t('announce.editTitle') : t('announce.createTitle')}
                </h2>
              </div>

              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('announce.fieldTitle')}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder={t('announce.titlePlaceholder')}
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('announce.fieldContent')}
                  </label>
                  <div className="bg-white dark:bg-gray-800 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                    <ReactQuill
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={t('announce.contentPlaceholder')}
                      style={{ minHeight: '200px' }}
                      theme="snow"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('announce.fieldType')}
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    >
                      {typeOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('announce.fieldPriority')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Target Roles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('announce.fieldRoles')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {roleOptions.map(role => (
                      <label key={role.value} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.target_roles.includes(role.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                target_roles: [...formData.target_roles, role.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                target_roles: formData.target_roles.filter(r => r !== role.value)
                              });
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{role.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('announce.fieldStartDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('announce.fieldEndDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('announce.fieldActive')}</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.show_on_dashboard}
                      onChange={(e) => setFormData({ ...formData, show_on_dashboard: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('announce.fieldShowDashboard')}</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.show_as_popup}
                      onChange={(e) => setFormData({ ...formData, show_as_popup: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('announce.fieldShowPopup')}</span>
                  </label>
                </div>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  {t('announce.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  {editingAnnouncement ? t('announce.update') : t('announce.create')}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      {/* View Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)}>
          {viewingAnnouncement && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-700 ${getTypeConfig(viewingAnnouncement.type).color}`}>
                  {React.createElement(getTypeConfig(viewingAnnouncement.type).icon, { className: 'h-5 w-5' })}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                    {viewingAnnouncement.title}
                  </h2>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {viewingAnnouncement.created_by_name} • {new Date(viewingAnnouncement.created_at).toLocaleString('tr-TR')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <Plus className="h-6 w-6 transform rotate-45" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 prose dark:prose-invert max-w-none">
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewingAnnouncement.content) }} />
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center rounded-b-lg">
              <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{t('announce.viewStart')} {viewingAnnouncement.start_date ? new Date(viewingAnnouncement.start_date).toLocaleDateString() : '-'}</span>
                <span>{t('announce.viewEnd')} {viewingAnnouncement.end_date ? new Date(viewingAnnouncement.end_date).toLocaleDateString() : '-'}</span>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {t('announce.close')}
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default AnnouncementsPage;